import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { ArrowUpRight, Clock, Calendar } from 'lucide-react';
import Eyebrow from './Eyebrow';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

// Full content for sample posts
const SAMPLE_POSTS = [
  {
    id: 's1',
    title: 'Practical AI Adoption: Where to Start',
    excerpt: 'Most AI pilots fail not because of technology but because of scope. Here\'s a framework for identifying the right first use case in your organization.',
    content: `Most AI pilots fail not because of the technology — but because of scope creep, wrong problem selection, and unrealistic timelines. After deploying AI across 15+ industries, we've distilled a repeatable framework for identifying the right first use case.

<strong>Start With Pain, Not Possibility</strong>

The most common mistake: teams get excited about what AI can do and try to apply it everywhere at once. Instead, start with your most painful, repetitive, high-volume problem. Ask: "What task consumes the most human hours but produces consistent, predictable outputs?" That's your first target.

<strong>The 3 Filters</strong>

1. <strong>Data availability</strong> — Does the process already generate enough data to train or prompt a model? If you're starting from scratch, expect 3-6 months of data collection before an AI model is viable.

2. <strong>Output measurability</strong> — Can you quantify whether the AI is performing well? "It feels better" is not a metric. "Time to first response dropped from 4 hours to 8 minutes" is.

3. <strong>Human-in-the-loop tolerance</strong> — Some processes tolerate AI errors (draft emails, summaries). Others cannot (medical diagnoses, financial compliance). Start with high-tolerance workflows.

<strong>The 90-Day Rule</strong>

If your AI project cannot show measurable results in 90 days, it's either the wrong problem or the wrong approach. Structure your first use case so you can run a meaningful A/B test within a single quarter.

<strong>Common First Wins</strong>

- Customer support ticket classification and routing
- Internal document Q&A (RAG on company knowledge base)
- Meeting summarization and action item extraction
- Lead scoring from CRM activity data
- Invoice processing and data extraction

None of these require custom models. All can be built with existing LLM APIs in 4-8 weeks.

The goal isn't to boil the ocean. It's to ship one thing that works, prove ROI, and build organizational trust in AI — then scale from there.`,
    category: 'AI Strategy', read_time: 6, created_at: '2026-05-20', author: 'Syenovix',
  },
  {
    id: 's2',
    title: 'Securing Your AI Stack: 7 Principles',
    excerpt: 'As LLMs enter production systems, attack surfaces expand. From prompt injection to data leakage — what every engineering team needs to know.',
    content: `The rapid adoption of LLMs in production systems has introduced a new class of vulnerabilities that traditional security frameworks weren't designed to handle. Here are 7 principles every engineering team must apply.

<strong>1. Treat Every User Input as Untrusted</strong>

LLMs are susceptible to prompt injection — where malicious input overwrites system instructions. Never concatenate user input directly into system prompts. Use structured input formats and validate all inputs before they reach the model.

<strong>2. Implement Output Validation</strong>

LLM outputs are non-deterministic. Always validate model outputs before they're used in downstream systems. If the model returns code that gets executed, treat it as untrusted code from an unknown source.

<strong>3. Apply Least-Privilege to Model Permissions</strong>

If your AI agent can call tools (read files, query databases, send emails), ensure each tool call requires explicit user confirmation for irreversible actions. Never give an AI agent write access to production systems without an approval gate.

<strong>4. Log Everything</strong>

Every prompt, every completion, every tool call. AI systems are complex and debugging requires full observability. Logs also provide an audit trail for compliance.

<strong>5. Isolate AI Workloads</strong>

Run AI inference in isolated environments. Don't co-locate AI workloads with systems holding sensitive PII unless strictly necessary. Use network policies to limit what AI components can reach.

<strong>6. Rate Limit and Monitor for Abuse</strong>

LLM endpoints are expensive to run and easy to abuse. Implement per-user rate limiting, cost alerts, and anomaly detection on token usage patterns.

<strong>7. Have a Kill Switch</strong>

Every AI feature should be behind a feature flag that can be disabled instantly without a deployment. When something goes wrong — and eventually it will — you need to be able to turn it off in seconds.

Security is not a phase. It's a continuous practice. Build these principles in from day one.`,
    category: 'Security', read_time: 8, created_at: '2026-05-12', author: 'Syenovix',
  },
  {
    id: 's3',
    title: 'Design Systems That Scale with Your Team',
    excerpt: 'A well-built design system cuts design-to-code time by 60% and eliminates entire categories of design debt. Here\'s how to build one that lasts.',
    content: `A design system is not a component library. A component library is part of a design system — but a design system is the shared language between design and engineering that governs every visual and interaction decision in your product.

<strong>Why Most Design Systems Fail</strong>

They're built by designers for designers, or by engineers for engineers — but never as a shared contract between both. The result: designers use Figma components that don't map to code, and engineers build components that drift from design intent within months.

<strong>The Foundation: Design Tokens</strong>

Before building any components, establish design tokens — named variables for colors, spacing, typography, shadows, and animation durations. Every visual value in the system should reference a token, not a hardcoded value.

Example token structure:
- color.brand.primary → #5E0ED7
- spacing.lg → 24px
- radius.card → 28px
- duration.standard → 200ms

When tokens change, everything updates. This is how you avoid "find and replace #5E0ED7 across 400 files."

<strong>Component Architecture</strong>

Build components in three layers:
1. <strong>Primitives</strong> — Button, Input, Text, Icon. No business logic. Fully composable.
2. <strong>Composites</strong> — Card, Form, Modal. Built from primitives.
3. <strong>Templates</strong> — Page-level layouts. Built from composites.

Never skip layers. The moment a Template contains a hardcoded color, your system starts to decay.

<strong>The Governance Problem</strong>

Design systems die from neglect, not from bad architecture. Assign clear ownership. Establish a contribution process. Run regular audits to catch component drift. Treat the design system as a product with its own roadmap.

<strong>Practical First Steps</strong>

1. Audit your existing product — identify the 20 components that cover 80% of your UI
2. Extract your token values from the current design
3. Build those 20 components to token spec
4. Ship it and get feedback from both designers and engineers
5. Iterate weekly

A design system is never done. The best ones are the ones that grow with the team.`,
    category: 'Product Design', read_time: 5, created_at: '2026-04-30', author: 'Syenovix',
  },
];

// Vibrant category card styles — gradient header + accent
const CATEGORY_STYLES = {
  'AI Strategy':             { gradient: 'linear-gradient(135deg, #5E0ED7 0%, #7c3aed 100%)', chip: '#ede9fe', chipText: '#5E0ED7', dot: '#7c3aed' },
  'Security':                { gradient: 'linear-gradient(135deg, #dc2626 0%, #e11d48 100%)', chip: '#fee2e2', chipText: '#dc2626', dot: '#e11d48' },
  'Product Design':          { gradient: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)', chip: '#d1fae5', chipText: '#059669', dot: '#0d9488' },
  'Engineering':             { gradient: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 100%)', chip: '#dbeafe', chipText: '#1d4ed8', dot: '#4f46e5' },
  'Digital Transformation':  { gradient: 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)', chip: '#fef3c7', chipText: '#d97706', dot: '#ea580c' },
};
const defaultStyle = { gradient: `linear-gradient(135deg, ${ACCENT} 0%, #7c3aed 100%)`, chip: '#ede9fe', chipText: ACCENT, dot: '#7c3aed' };

function BlogCard({ post, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const navigate = useNavigate();
  const style = CATEGORY_STYLES[post.category] || defaultStyle;

  const readTime = post.read_time || post.readTime || 5;
  const date = post.created_at || post.date || '';

  function handleClick() {
    // Navigate and pass full post as state (works for both sample + DB posts)
    navigate(`/blog/${post.id}`, { state: { post } });
  }

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, duration: 0.55, ease }}
      whileHover={{ y: -6, scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={handleClick}
      className="cursor-pointer h-full"
      style={{ transition: 'box-shadow 200ms ease' }}
    >
      {/* Outer shell */}
      <div className="rounded-[1.75rem] h-full overflow-hidden" style={{ boxShadow: '0 4px 24px -6px rgba(0,0,0,0.10)' }}>

        {/* Gradient header */}
        <div className="h-32 relative flex items-end p-5" style={{ background: style.gradient }}>
          <span
            className="text-[10px] font-bold tracking-[0.1em] uppercase px-3 py-1 rounded-full"
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', backdropFilter: 'blur(8px)' }}
          >
            {post.category || 'Insights'}
          </span>
          {/* Decorative circles */}
          <div className="absolute top-4 right-4 w-16 h-16 rounded-full opacity-20" style={{ background: 'rgba(255,255,255,0.3)' }} />
          <div className="absolute top-8 right-10 w-8 h-8 rounded-full opacity-10" style={{ background: '#fff' }} />
        </div>

        {/* Card body */}
        <div className="bg-white p-6 flex flex-col gap-3" style={{ minHeight: 220 }}>
          {/* Read time + date */}
          <div className="flex items-center gap-3 text-[11px]" style={{ color: '#A0A0A7' }}>
            <span className="flex items-center gap-1"><Clock size={11} />{readTime} min read</span>
            {date && <span className="flex items-center gap-1"><Calendar size={11} />{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
          </div>

          {/* Title */}
          <h3
            className="text-[15px] font-bold leading-snug tracking-tight"
            style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="text-sm leading-relaxed flex-1" style={{ color: '#6B6B72' }}>
            {post.excerpt}
          </p>

          {/* Read more CTA */}
          <div className="flex items-center justify-between pt-3 mt-auto" style={{ borderTop: '1px solid rgba(24,24,27,0.06)' }}>
            <span className="text-xs font-medium" style={{ color: '#A0A0A7' }}>By {post.author || 'Syenovix'}</span>
            <motion.span
              className="flex items-center gap-1 text-[12px] font-bold rounded-full px-3 py-1.5"
              style={{ background: style.chip, color: style.chipText }}
              whileHover={{ gap: '6px' }}
            >
              Read More <ArrowUpRight size={11} />
            </motion.span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState(SAMPLE_POSTS);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    api.getBlogPosts()
      .then(data => {
        if (data?.length) {
          setPosts(data.map(p => ({
            ...p,
            readTime: p.read_time || p.readTime || 5,
            created_at: p.created_at?.split('T')[0] || p.date,
          })));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section id="blog" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-gray" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto w-full">
        <div ref={ref} className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
              <Eyebrow>Insights & Blog</Eyebrow>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.08, ease }}
              className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight max-w-xl leading-[1.08]"
              style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
            >
              Ideas That Move Technology Forward
            </motion.h2>
          </div>
          <motion.a
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.16, ease }}
            href="#contact"
            className="flex items-center gap-1.5 text-sm font-semibold tracking-widest uppercase whitespace-nowrap"
            style={{ color: ACCENT }}
          >
            All Articles <ArrowUpRight size={14} />
          </motion.a>
        </div>

        {/* Equal-height grid — shows all posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {posts.map((post, i) => (
            <BlogCard key={post.id} post={post} index={i} />
          ))}
        </div>

        {posts.length === 0 && (
          <div className="text-center py-20" style={{ color: '#A0A0A7' }}>
            <p className="text-sm">No posts published yet. Add posts from the admin dashboard.</p>
          </div>
        )}
      </div>
    </section>
  );
}
